import { useCallback, useEffect, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';

import { RetailerSearchResult as Retailer } from '@packages/models/api';
import { analyticsService, qmrsService, techlineService } from '@web/services/singletons';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { useTranslation } from 'react-i18next';
import { useFormContext } from '../battery-context';
import { Typography } from '@packages/ui/shared';
import { Typeahead } from 'react-bootstrap-typeahead';
import { ANALYTICS_EVENTS } from '@packages/core/analytics';

const logger = getLogger('vin-sessions');

type StepTwoContentProps = {
    currentStep: number;
    displayQrCodeScanner: boolean;
};

const useStyles = createUseStyles({
    header: {
        padding: '16px 24px!important',
    },
    title: {
        fontSize: '21px!important',
        fontWeight: '600!important',
    },
    body: {
        padding: '16px 24px!important',
    },
    bodyDescription: {
        fontSize: '15px!important',
        fontWeight: '400!important',
    },
    footer: {
        padding: '24px!important',
    },
    formLabel: {
        fontSize: '15px',
    },
    headerSearchRetailer: {
        minWidth: 256,
    },
    attachments: {
        position: 'relative',
        height: 237,
        width: 364,
        border: '1px solid #999DA3',
        borderRadius: '4px',
    },
});

const StepTwoContent: React.FC<StepTwoContentProps> = ({ currentStep, displayQrCodeScanner }) => {
    const { t } = useTranslation();
    const [displayThisStep, setDisplayThisStep] = useState(false);
    const [allRetailers, setAllRetailers] = useState<Retailer[] | any[]>([]);
    const [validated, setValidated] = useState(false);
    const [inValid, setInValid] = useState(false);

    const [repairOrderNumber, setRepairOrderNumber] = useState('');

    const [selectedRetailer, setSelectedRetailer] = useState<any>();
    const { abortSignalRef, signalAbort } = useAbortController();

    const { updateData, data, errorMessage } = useFormContext();
    useEffect(() => {
        // set display state based on props
        setDisplayThisStep(currentStep === 2 && !displayQrCodeScanner);
    }, [currentStep, displayQrCodeScanner]);

    useEffect(() => {
        updateData({
            repairOrderNumber,
            selectedRetailer,
        });
    }, [repairOrderNumber, selectedRetailer]);

    useEffect(() => {
        fetchAllRetailers('');
    }, []);

    const clearSelection = async () => {
        signalAbort();
        setSelectedRetailer([]);
        await fetchSearchHistory();
    };

    const fetchSearchHistory = async () => {
        try {
            const response = await techlineService.recentSearch(false, abortSignalRef.current);
            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                throw response.data;
            }
            if (response.data.retailerSearch.length) {
                setAllRetailers(
                    response.data.retailerSearch.map((d: { name: any; code: any }) => ({
                        retailerName: d.name,
                        retailerNumber: d.code,
                    }))
                );
            }
        } catch (err) {
            logger.warn('Failed to fetch the recent retailer search history', { error: err });
        }
    };

    const fetchAllRetailers = async (query: string) => {
        try {
            if (!query.replace(/\s/g, '').length) {
                await clearSelection();
                return;
            }

            const response = await qmrsService.fetchRetailers({
                searchParams: { query, canCreateQmr: true },
                signal: abortSignalRef.current,
                ignoreCache: true,
            });
            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                throw response.data;
            }
            if (response.data.retailers.length) {
                const searched = response.data.retailers.map((d) => ({
                    retailerName: d.name,
                    retailerNumber: d.code,
                }));
                setAllRetailers(searched);
            }
        } catch (e) {
            alert('Error: ' + e + '. Please refresh & try again.');
            logger.error(`Failed to fetch the retailers for the query [${query}]`, { error: e, query });
        }
    };

    function handleRetailerTypeaheadChange(selected: Retailer[]) {
        setSelectedRetailer(selected);
        if (selected.length > 0) {
            analyticsService.logEvent(ANALYTICS_EVENTS.USER_SELECTS_RETAILER);
        }
    }

    const handleInputChange = useCallback(
        async (text: string) => {
            await fetchAllRetailers?.(text);
        },
        [fetchAllRetailers]
    );

    if (!displayThisStep) return null;

    return (
        <Modal.Body>
            <div className="pb-4">
                <div className="pb-2">
                    <Typography variant="labelRegular">
                        Retailer
                        <Typography color="redOne"> *</Typography>
                    </Typography>
                </div>
                <Typeahead
                    id="retailer-typeahead"
                    placeholder="Select Retailer"
                    options={allRetailers}
                    labelKey={(option) => `${option.retailerName} (#${option.retailerNumber})`}
                    onChange={handleRetailerTypeaheadChange}
                    onInputChange={handleInputChange}
                    selected={selectedRetailer}
                />
                <Form.Control
                    isInvalid={errorMessage.selectedRetailer?.length > 0}
                    style={{ display: 'none' }}
                ></Form.Control>
                {errorMessage.selectedRetailer?.length > 0 && (
                    <Form.Control.Feedback type="invalid">{errorMessage.selectedRetailer}</Form.Control.Feedback>
                )}
            </div>
            <div className="pb-4">
                <div className="pb-2">
                    <Typography variant="labelRegular">
                        {t('Repair Order Number')}
                        <Typography color="redOne"> *</Typography>
                    </Typography>
                </div>
                <Form.Group controlId="repairOrderNumber">
                    <Form.Control
                        onChange={(event) => {
                            const value = event.target.value;
                            const hasSpecialChar = /[^A-Za-z0-9]/.test(value);

                            setValidated(true);

                            if (hasSpecialChar) {
                                setInValid(true);
                                return;
                            } else {
                                setInValid(false);
                                setRepairOrderNumber(value); // update state only if valid
                            }
                        }}
                        name="repairOrderNumber"
                        type="text"
                        placeholder={t('Enter repair order number')}
                        maxLength={10}
                        value={data.repairOrderNumber}
                        required
                        minLength={4}
                        pattern="^[A-Za-z0-9]+$"
                        isInvalid={
                            inValid ||
                            (validated && data.repairOrderNumber && data.repairOrderNumber.length
                                ? data.repairOrderNumber?.length < 4 || data.repairOrderNumber?.length > 10
                                : false)
                        }
                    />
                    <Form.Control.Feedback type="invalid">
                        Repair Order Number must be alphanumeric 4-10 characters.
                    </Form.Control.Feedback>
                </Form.Group>
            </div>
        </Modal.Body>
    );
};

export default StepTwoContent;
